import type { CSSProperties, ReactNode } from 'react'
import { C, action } from '../../theme'
import { splitPx } from '../../lib/format'

/** Кликабельная подпись. Кнопок в интерфейсе нет — он зеркало, а не пульт. */
export function Action({
  children,
  onClick,
  style,
}: {
  children: ReactNode
  onClick: () => void
  style?: CSSProperties
}) {
  return (
    <span onClick={onClick} style={{ ...action, ...style }}>
      {children}
    </span>
  )
}

/** Переключатель окна / рынка / фильтра. */
export function Chip({
  children,
  active,
  onClick,
  pad = '2px 9px',
}: {
  children: ReactNode
  active: boolean
  onClick: () => void
  pad?: string
}) {
  return (
    <span
      onClick={onClick}
      style={{
        cursor: 'pointer',
        fontSize: 13,
        padding: pad,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        border: `1px solid ${active ? C.accent : C.border}`,
        color: active ? '#FFFFFF' : C.muted2,
        background: active ? C.accent : '#FFFFFF',
      }}
    >
      {children}
    </span>
  )
}

/**
 * Восьмизначная цена: значащая часть контрастная, хвост приглушён.
 * Без этого таблица направлений нечитаема.
 */
export function Px({ value, tailColor = C.muted }: { value: number; tailColor?: string }) {
  const [head, tail] = splitPx(value)
  return (
    <>
      {head}
      <span style={{ color: tailColor }}>{tail}</span>
    </>
  )
}

/**
 * «Нет данных» — отдельное состояние, никогда не ноль. В этой системе ноль
 * чаще означает «не измеряем», чем «не происходит».
 */
export function NoData({ label = 'no data' }: { label?: string }) {
  return <span style={{ color: C.faint }}>{label}</span>
}

/** Заголовок крупного раздела: подпись + синяя черта в 4px. */
export function SectionHead({
  title,
  right,
  paddingTop = 36,
}: {
  title: string
  right?: ReactNode
  paddingTop?: number
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        rowGap: 6,
        flexWrap: 'wrap',
        borderBottom: `4px solid ${C.accent}`,
        padding: `${paddingTop}px 0 14px`,
      }}
    >
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: '.22em',
          textTransform: 'uppercase',
          color: C.accent,
        }}
      >
        {title}
      </span>
      {right}
    </div>
  )
}

/** Заголовок обычного блока: подпись + тонкая серая черта. */
export function BlockHead({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        rowGap: 6,
        flexWrap: 'wrap',
        borderBottom: `1px solid ${C.border}`,
        paddingBottom: 12,
      }}
    >
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: '.22em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </span>
      {right}
    </div>
  )
}

/** Раскрывающаяся панель на светло-сером фоне. */
export function Drawer({ children, marginTop = 22 }: { children: ReactNode; marginTop?: number }) {
  return (
    <div
      style={{
        background: C.panel,
        border: `1px solid ${C.border}`,
        marginTop,
        padding: '18px 20px 20px',
      }}
    >
      {children}
    </div>
  )
}

/**
 * Пояснение под блоком. Это не украшение: именно здесь живут знаменатели и
 * оговорки, без которых число на экране врёт.
 */
export function Note({ children, maxWidth = 1020 }: { children: ReactNode; maxWidth?: number }) {
  return (
    <div
      style={{
        fontSize: 14,
        color: C.muted2,
        lineHeight: 1.6,
        marginTop: 14,
        maxWidth,
        textWrap: 'pretty',
      }}
    >
      {children}
    </div>
  )
}

export function Strong({ children }: { children: ReactNode }) {
  return <span style={{ color: C.text }}>{children}</span>
}
